// Room desire calculation - makes rooms feel "alive"

import type { Room, Corridor } from "../types.ts";

export type DesireType = 'needs-label' | 'wants-connection';

export interface RoomDesire {
  type: DesireType;
  strength: number; // 0-1, how strongly the room wants this
  direction?: { x: number; y: number }; // For connection desires, which way
}

export interface DesireState {
  roomDesires: Map<string, RoomDesire[]>;
  lastUpdated: number;
}

/**
 * Calculate desires for all rooms in the dungeon
 */
export function calculateDesires(
  rooms: Map<string, Room>,
  corridors: Map<string, Corridor>
): Map<string, RoomDesire[]> {
  const desires = new Map<string, RoomDesire[]>();

  // Build connection count per room
  const connectionCount = new Map<string, number>();
  for (const room of rooms.values()) {
    connectionCount.set(room.id, 0);
  }
  for (const corridor of corridors.values()) {
    for (const roomId of corridor.connects) {
      const current = connectionCount.get(roomId) || 0;
      connectionCount.set(roomId, current + 1);
    }
  }

  for (const room of rooms.values()) {
    const roomDesires: RoomDesire[] = [];

    // Unlabeled rooms want labels (noticeable strength)
    const hasLabel = room.label && room.label !== room.id && room.label.trim() !== '';
    if (!hasLabel && room.hexes.size >= 2) {
      roomDesires.push({
        type: 'needs-label',
        strength: 0.6, // Noticeable but not overwhelming
      });
    }

    // Dead-end rooms want connections
    const connections = connectionCount.get(room.id) || 0;
    if (connections === 0 && rooms.size > 1) {
      // Isolated room - strongly wants connection
      roomDesires.push({
        type: 'wants-connection',
        strength: 0.5,
      });
    } else if (connections === 1 && room.hexes.size >= 3) {
      // Dead-end - mildly wants another connection
      roomDesires.push({
        type: 'wants-connection',
        strength: 0.3,
      });
    }

    if (roomDesires.length > 0) {
      desires.set(room.id, roomDesires);
    }
  }

  return desires;
}

/**
 * Check if a room has a specific desire
 */
export function hasDesire(
  desires: Map<string, RoomDesire[]>,
  roomId: string,
  type: DesireType
): boolean {
  const roomDesires = desires.get(roomId);
  if (!roomDesires) return false;
  return roomDesires.some(d => d.type === type);
}

/**
 * Get the strongest desire for a room
 */
export function getStrongestDesire(
  desires: Map<string, RoomDesire[]>,
  roomId: string
): RoomDesire | null {
  const roomDesires = desires.get(roomId);
  if (!roomDesires || roomDesires.length === 0) return null;
  return roomDesires.reduce((a, b) => a.strength > b.strength ? a : b);
}
